import React from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@material-ui/core';
import { currencyFormatter } from 'utils/prices/formatter';
import useStyles from 'pages/widget/components/product-short-info/styles';


const getRentalPrice = (prices, initialSelectedCurrency, isAvailableEmptyPrice) => {

  if (!prices || !Array.isArray(prices) || prices.length === 0) {
    return {
      price: currencyFormatter(0),
      currency: initialSelectedCurrency
    };
  }

  let priceValue = 0;
  let selectedCurrency = initialSelectedCurrency;

  const priceObj = prices.find(
    p => p.currency?.currency === initialSelectedCurrency && parseFloat(p.value) > 0
  );
  
  priceValue = priceObj?.value || 0;

  if (!isAvailableEmptyPrice && !parseFloat(priceValue)) {
    const fallbackCurrencies = ['NOK', 'EUR', 'USD'];
    
    for (const currency of fallbackCurrencies) {
      if (currency === initialSelectedCurrency) continue;
      
      const fallbackPriceObj = prices.find(
        p => p.currency?.currency === currency && parseFloat(p.value) > 0
      );
      
      if (fallbackPriceObj && parseFloat(fallbackPriceObj.value)) {
        priceValue = fallbackPriceObj.value;
        selectedCurrency = currency;
        break;
      }
    }
  }

  return {
    price: currencyFormatter(priceValue),
    currency: selectedCurrency
  };
};


const getRentalPriceRange = (prices, currency) => {
  if (!prices?.length) return null;

  const relevantPrices = prices
    .filter(p => p.currency?.currency === currency && parseFloat(p.value) > 0)
    .map(p => parseFloat(p.value));

  if (!relevantPrices.length) return null;

  const minPrice = Math.min(...relevantPrices);
  const maxPrice = Math.max(...relevantPrices);

  if (minPrice === maxPrice) {
    return {
      single: currencyFormatter(minPrice),
      isRange: false
    };
  }

  return {
    min: currencyFormatter(minPrice),
    max: currencyFormatter(maxPrice),
    isRange: true
  };
};


const RentalDailyPrices = ({ prices, initialSelectedCurrency, isAvailableEmptyPrice }) => {
  const [t] = useTranslation();
    const classes =  useStyles();

  if (!prices?.length) return null;

  
  const pricesByDay = {};
  prices.forEach(price => {
    if (price.day?.name) {
      if (!pricesByDay[price.day.name]) {
        pricesByDay[price.day.name] = [];
      }
      pricesByDay[price.day.name].push(price);
    }
  });

  const days = Object.keys(pricesByDay);
  
  if (days.length === 0) {
    
    const { price, currency } = getRentalPrice(
      prices,
      initialSelectedCurrency,
      isAvailableEmptyPrice
    );
    return (
      <div className={classes?.currency}>
        {currency} {price}
      </div>
    );
  }

  return (
    <div className={classes?.pricePerDayWrapper}>
      {days.map((dayName, index) => {
        const dayPrices = pricesByDay[dayName];
        const { price, currency } = getRentalPrice(
          dayPrices,
          initialSelectedCurrency,
          isAvailableEmptyPrice
        );
        const isLastItem = index === days.length - 1;

        return (
          <div key={dayName}>
            <Typography variant="h3" className={classes?.pricePerDayTitle}>
              {t(dayName)}:{' '}
            </Typography>
            <span className={classes?.currency}>
              {currency} {price}
            </span>
            {!isLastItem && <span>&nbsp;•&nbsp;</span>}
          </div>
        );
      })}
    </div>
  );
};


const RentalHourlyPrices = ({ prices, initialSelectedCurrency, isAvailableEmptyPrice }) => {
    const [t] = useTranslation();
    const classes =  useStyles();

  if (!prices?.length) return null;

  const { price, currency } = getRentalPrice(
    prices,
    initialSelectedCurrency,
    isAvailableEmptyPrice
  );

  const priceRange = getRentalPriceRange(prices, currency);

  return (
    <div className={classes?.currency}>
      {priceRange?.isRange ? (
        <>
          {currency} {priceRange.min} - {priceRange.max}
        </>
      ) : (
        <>
          {currency} {price}
        </>
      )}
    </div>
  );
};


export const RentalPrices = ({
  prices,
  pricesType,
  initialSelectedCurrency,
  isAvailableEmptyPrice
}) => {
  const classes =  useStyles(); 
  const [t] = useTranslation();

  if (!prices) return null;

  switch (pricesType) {
    case 'pricesFixedForm': {
      const { price, currency } = getRentalPrice(
        prices,
        initialSelectedCurrency,
        isAvailableEmptyPrice
      );
      return (
        <div className={classes.currency}>
          {currency} {price}
        </div>
      );
    }

    case 'pricesDailyForm': {
      return (
        <RentalDailyPrices
          prices={prices}
          initialSelectedCurrency={initialSelectedCurrency}
          isAvailableEmptyPrice={isAvailableEmptyPrice}
          classes={classes}
        />
      );
    }

    case 'pricesWeeklyForm': {
     
      const weekdayPrices = prices.filter(p => p.type === 'WEEKDAY');
      const weekendPrices = prices.filter(p => p.type === 'WEEKEND');

      const hasWeekdayPrices = weekdayPrices.some(p => parseFloat(p.value) > 0);
      const hasWeekendPrices = weekendPrices.some(p => parseFloat(p.value) > 0);

      if (!hasWeekdayPrices && !hasWeekendPrices) {
        const { price, currency } = getRentalPrice(
          prices,
          initialSelectedCurrency,
          isAvailableEmptyPrice
        );
        return (
          <div className={classes.currency}>
            {currency} {price}
          </div>
        );
      }

      return (
        <div>
          {hasWeekdayPrices && (
            <div>
              <Typography variant="h3">{t('Weekday')}:</Typography>
              <span className={classes.currency}>
                {getRentalPrice(weekdayPrices, initialSelectedCurrency, isAvailableEmptyPrice).currency}{' '}
                {getRentalPrice(weekdayPrices, initialSelectedCurrency, isAvailableEmptyPrice).price}
              </span>
            </div>
          )}
          {hasWeekendPrices && (
            <div>
              <Typography variant="h3">{t('Weekend')}:</Typography>
              <span className={classes.currency}>
                {getRentalPrice(weekendPrices, initialSelectedCurrency, isAvailableEmptyPrice).currency}{' '}
                {getRentalPrice(weekendPrices, initialSelectedCurrency, isAvailableEmptyPrice).price}
              </span>
            </div>
          )}
        </div>
      );
    }

    case 'pricesHourlyForm': {
      return (
        <RentalHourlyPrices
          prices={prices}
          initialSelectedCurrency={initialSelectedCurrency}
          isAvailableEmptyPrice={isAvailableEmptyPrice}
          classes={classes}
        />
      );
    }

    default:
      return null;
  }
};

export default RentalPrices;